import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  activityListingData: {
    activitiesFullData: [],
    total: 0,
    skip: 0,
    take: 0,
    page: 1,
    status: '',
    sortDir: 'DESC',
    sortBy: 1,
  },
  activityListingTabs: {
    openCount: 0,
    closeCount: 0,
    unassignedCount: 0,
    watchingCount: 0,
  },
  pagingModel: {
    Page: 0,
    TotalCount: 0,
    Rows: 0,
    MaxPageSize: 0,
    PageSizeInterval: 0,
  },
  bulkAction: {
    showConfirmationBox: false,
    validaActivity: null,
    validAcceptReassignActivity: null,
    totalActivitysCount: 0,
    validActivitysCount: 0,
    validActivtyId: null,
  },
  isLoading: false,
  error: null,
};

const activityListingSlice = createSlice({
  name: 'activityListing',
  initialState,
  reducers: {
    fetchActivityListingDataRequest: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchActivityListingDataSuccess: (state, action) => {
      state.isLoading = false;
      const { isScroll, ...data } = action.payload;
      
      if (isScroll) {
        state.activityListingData = {
          ...data,
          activitiesFullData: [
            ...state.activityListingData.activitiesFullData,
            ...data.activitiesFullData,
          ],
        };
      } else {
        state.activityListingData = data;
      }
    },
    fetchActivityListingDataFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    updatePagingModel: (state, action) => {
      state.pagingModel = {
        ...state.pagingModel,
        ...action.payload,
      };
    },

    resetActivityListingData: (state) => {
      state.activityListingData = initialState.activityListingData;
    },

    updateTabCount: (state, action) => {
      state.activityListingTabs = {
        ...state.activityListingTabs,
        ...action.payload,
      };
    },

    activityListingBulkActionSuccess: (state, action) => {
      state.bulkAction = {
        ...state.bulkAction,
        ...action.payload,
      };
    },

    activityListingBulkActionCancel: (state) => {
      state.bulkAction = initialState.bulkAction;
    },

    getAcceptReassignDataSuccess: (state, action) => {
      state.bulkAction = {
        ...state.bulkAction,
        ...action.payload,
      };
    },

    bulkValidationAcceptReassignActivitySuccess: (state, action) => {
      state.bulkAction = {
        ...state.bulkAction,
        totalActivitysCount: action.payload.TotalCount,
        validActivitysCount: action.payload.ValidCount,
        validActivtyId: action.payload.ValidActIds,
      };
    },

    resetAcceptReassignActivity: (state) => {
      state.bulkAction = initialState.bulkAction;
    },

    bulkValidateActivityIdsChangeAcceptReassignRequestSuccess: (state, action) => {
      // This is handled in saga with redirect
    },

    // Bulk action handlers
    activityListingBulkActionHandler: (state, action) => {
      // Handled in saga
    },
    activityListingBulkActionConfirm: (state, action) => {
      // Handled in saga
    },
    getAcceptReassignDataRequest: (state, action) => {
      // Handled in saga
    },
    bulkValidationAcceptReassignActivityRequest: (state, action) => {
      // Handled in saga
    },
    bulkAcceptReassignActivityRequest: (state, action) => {
      // Handled in saga
    },
  },
});

export const {
  fetchActivityListingDataRequest,
  fetchActivityListingDataSuccess,
  fetchActivityListingDataFailure,
  updatePagingModel,
  resetActivityListingData,
  updateTabCount,
  activityListingBulkActionSuccess,
  activityListingBulkActionCancel,
  getAcceptReassignDataSuccess,
  bulkValidationAcceptReassignActivitySuccess,
  resetAcceptReassignActivity,
  bulkValidateActivityIdsChangeAcceptReassignRequestSuccess,
  activityListingBulkActionHandler,
  activityListingBulkActionConfirm,
  getAcceptReassignDataRequest,
  bulkValidationAcceptReassignActivityRequest,
  bulkAcceptReassignActivityRequest,
} = activityListingSlice.actions;

export default activityListingSlice.reducer;

const enrichActivityWithUserData = (activity, userList) => {
  if (!activity) return activity;
  
  const owner = activity.activityOwnerId && userList[activity.activityOwnerId];
  const assignee = activity.activityAssigneeId && userList[activity.activityAssigneeId];
  
  // Build full name from firstName and lastName
  const ownerFullName = owner 
    ? `${owner.first_name || owner.firstName || ''} ${owner.last_name || owner.lastName || ''}`.trim()
    : '';
  const assigneeFullName = assignee
    ? `${assignee.first_name || assignee.firstName || ''} ${assignee.last_name || assignee.lastName || ''}`.trim()
    : '';
  
  return {
    ...activity,
    activityOwnerName: ownerFullName || activity.activityOwnerName || '',
    activityOwnerImage: owner?.ProfileFullImage || owner?.profile_picture || activity.activityOwnerImage || '',
    activityAssignee: assigneeFullName || activity.activityAssignee || '',
    activityAssigneeImage: assignee?.ProfileFullImage || assignee?.profile_picture || activity.activityAssigneeImage || '',
  };
};

export const selectActivityListingData = (state) => state.activityListing.activityListingData;

export const selectEnrichedActivityListingData = (state) => {
  const activityData = state.activityListing.activityListingData;
  const userList = state.userList || {};
  
  if (!activityData?.activitiesFullData) return activityData;
  
  return {
    ...activityData,
    activitiesFullData: activityData.activitiesFullData.map(activity => 
      enrichActivityWithUserData(activity, userList)
    ),
  };
};

export const selectPagingModel = (state) => state.activityListing.pagingModel;
export const selectBulkAction = (state) => state.activityListing.bulkAction;
export const selectIsLoading = (state) => state.activityListing.isLoading;
export const selectError = (state) => state.activityListing.error;
